import { useState, useEffect, useRef } from 'react';
import { toast } from "sonner";
import { Form, Button, Dropdown, Modal } from 'react-bootstrap';

import { FaSortAlphaDown, FaSortAlphaUp, FaFilter, FaRedo } from 'react-icons/fa';
import { deleteTransaction, getTransactionByAccountId, getTransactionById } from '../service/api/TransactionService';
import useSelectedDomain from '../service/Hooks/useSelectedDomain';
import { useUserData } from '../service/api/useUserData';
import ConfirmationModal from '../Modal/ConfirmationModal';

export default function TransactionClientTable() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const selectedDomain = useSelectedDomain();
    const [currentPage, setCurrentPage] = useState(1);
    const [transactionsPerPage] = useState(5);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
    const [selectedDateFilter, setSelectedDateFilter] = useState('');
    const [sortOrder, setSortOrder] = useState(''); // '' pour aucun tri, 'asc_qty' ou 'desc_qty'
    const [noTransactionsMessage, setNoTransactionsMessage] = useState(null);
    const timerRef = useRef(null);

    // Nouvel état pour la modal
    const [showModal, setShowModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [transactionToDelete, setTransactionToDelete] = useState(null);

    // gerer l'id de l'utilisateur connecte
    const { getUserInfo } = useUserData();
    const [creatorId, setCreatorId] = useState(null);

    useEffect(() => {
        async function fetchUserData() {
            const userInfo = await getUserInfo();
            if (userInfo) {
                setCreatorId(userInfo.accountId);
            }
        }
        fetchUserData();
    }, []);
    // fin de la gestion de l'id de l'utilisateur connecte
    useEffect(() => {
        if (noTransactionsMessage) {
            timerRef.current = setTimeout(() => {
                setNoTransactionsMessage(null);
            }, 5000);
        }
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [noTransactionsMessage]);

    useEffect(() => {
        if (!selectedDomain || !selectedDomain.id || !creatorId) {
            return;
        }
        const fetchTransactions = async () => {
            try {
                const data = await getTransactionByAccountId(creatorId, selectedDomain.id);
                // console.log("Données récupérées :", data);
                setTransactions(data);
                setLoading(false);
                setCurrentPage(1);
            } catch (err) {
                setError('Une erreur est survenue lors de la récupération des données.');
                setLoading(false);
            }
        };
    
        fetchTransactions();
    }, [selectedDomain, creatorId]); // Ajout de creatorId ici
    
    // Fonction pour filtrer et trier les transactions
    const filterAndSortTransactions = () => {
        let filteredAndSorted = [...transactions];
        // Filtrer par statut
        if (selectedStatusFilter) {
            filteredAndSorted = filteredAndSorted.filter(transaction => transaction.status === selectedStatusFilter);
        }

        // Filtrer par date
        if (selectedDateFilter) {
            const today = new Date();
            let startDate;

            switch (selectedDateFilter) {
                case 'today':
                    startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    break;
                case 'this_week':
                    const firstDayOfWeek = today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1);
                    startDate = new Date(today.getFullYear(), today.getMonth(), firstDayOfWeek);
                    break;
                case 'this_month':
                    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                    break;
                default:
                    break;
            }
            if (startDate) {
                filteredAndSorted = filteredAndSorted.filter(transaction => new Date(transaction.creationDate) >= startDate);
            }
        }
        // Trier par quantité
        if (sortOrder === 'asc_qty') {
            filteredAndSorted.sort((a, b) => a.quantity - b.quantity);
        } else if (sortOrder === 'desc_qty') {
            filteredAndSorted.sort((a, b) => b.quantity - a.quantity);
        }

        return filteredAndSorted;
    };

    const filteredAndSortedTransactions = filterAndSortTransactions();

    // Pagination (appliquée après le filtrage et le tri)
    const indexOfLastTransaction = currentPage * transactionsPerPage;
    const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
    const currentTransactions = filteredAndSortedTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);

    const totalPages = Math.ceil(filteredAndSortedTransactions.length / transactionsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleStatusFilterChange = (event) => {
        setSelectedStatusFilter(event.target.value);
        setCurrentPage(1); // Reset page on filter change
    };

    const handleDateFilterChange = (event) => {
        setSelectedDateFilter(event.target.value);
        setCurrentPage(1); // Reset page on filter change
    };

    const handleSortOrderChange = (type) => {
        setSortOrder(type);
    };

    const handleResetFilters = () => {
        setSelectedStatusFilter('');
        setSelectedDateFilter('');
        setSortOrder('');
        setCurrentPage(1);
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'Completed':
                return 'bg-success text-white';
            case 'Pending':
                return 'bg-warning text-dark';
            case 'Canceled':
                return 'bg-danger text-white';
            case 'Partially':
                return 'bg-info text-white';
            default:
                return 'bg-secondary text-white';
        }
    };
    
    // supprimer une transaction
    const handleDeleteClick = transaction => {
        setTransactionToDelete(transaction);
        setShowDeleteModal(true);
    }
    const handleCancelDelete = () => {
        setTransactionToDelete(null);
        setShowDeleteModal(false);
    }
    const handleConfirmationDelete = async () => {
        try {
            await deleteTransaction(transactionToDelete.id);
            setTransactions(prev =>
            prev.filter(t => t.id !== transactionToDelete.id)
            );
            toast.success("Transaction supprimée avec succès.");
        } catch (error) {
            toast.error("Erreur lors de la suppression de la transaction.");
            // console.error(error);
        } finally {
            setTransactionToDelete(null);
            setShowDeleteModal(false);
        }
    };
    // Fonction pour gérer l'affichage de la modal et récupérer les détails
    const handleViewTransaction = async (transactionId) => {
        try {
            const transactionDetails = await getTransactionById(transactionId);
            setSelectedTransaction(transactionDetails);
            setShowModal(true);
        } catch (err) {
            // console.error("Erreur lors de la récupération des détails de la transaction:", err);
            toast.error("Erreur lors de la récupération des détails de la transaction.");
        }
    };

    const handleCloseModal = () => setShowModal(false);

    return (
        <div>
            <div className="row">
                <div className="col-xl-12">
                    <div className="card lastest_trans">
                        <div className="card-header border-0 pb-0 flex-wrap">
                            <div>
                                <h2 className="card-title mb-0">Liste des Transactions</h2>
                            </div>

                            <div className="d-flex align-items-center mb-4">
                                {/* Filtre par statut */}
                                <Form.Select
                                    value={selectedStatusFilter}
                                    onChange={handleStatusFilterChange}
                                    className="form-select ps-4 fw-bold border-primary shadow-sm me-2"
                                >
                                    <option value="">Tous les statuts</option>
                                    <option value="Completed">Complété</option>
                                    <option value="Pending">En attente</option>
                                    <option value="Canceled">Annulé</option>
                                </Form.Select>

                                {/* Filtre par date */}
                                <Form.Select
                                    value={selectedDateFilter}
                                    onChange={handleDateFilterChange}
                                    className="form-select ps-4 fw-bold border-primary shadow-sm me-2"
                                >   
                                    <option value="">Toutes les dates</option>
                                    <option value="today">Aujourd'hui</option>
                                    <option value="this_week">Cette semaine</option>
                                    <option value="this_month">Ce mois-ci</option>
                                </Form.Select>

                                {/* Trier par quantité */}
                                <Dropdown className="me-2">
                                    <Dropdown.Toggle variant="outline-primary">
                                        Quantité <FaFilter />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => handleSortOrderChange('asc_qty')}>
                                            Croissant <FaSortAlphaDown />
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => handleSortOrderChange('desc_qty')}>
                                            Décroissant <FaSortAlphaUp />
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>

                                {/* Bouton Réinitialiser */}
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleResetFilters}
                                >
                                  <FaRedo />
                                </Button>
                            </div>
                        </div>

                        <div className="card-body px-3 py-0">
                            <div className="table-responsive">
                                {loading && <div className="text-center">Chargement...</div>}
                                {error && <div className="text-danger text-center">{error}</div>}

                                {!loading && !error && currentTransactions.length > 0 ? (
                                    <table className="table trans-table border-no tbl-btn short-one">
                                        <thead>
                                            <tr>
                                                <th>Créateur</th>
                                                <th>Compte principal</th>
                                                <th>Compte destinataire</th>
                                                <th>Side</th>
                                                <th>Quantité</th>
                                                <th>Prix</th>
                                                <th>Date Création</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentTransactions.map((transaction) => (
                                                <tr className="trans-td-list" key={transaction.id}>
                                                    <td>
                                                        <div className="user-info">
                                                            <h6 className="mb-0 ms-2">{transaction.creatorName}</h6>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="user-info">
                                                            <h6 className="mb-0 ms-2">{transaction.fromAccountEmail}</h6>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="user-info">
                                                            <h6 className="mb-0 ms-2">{transaction.toAccountEmail}</h6>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge rounded bg-${transaction.side === 'Buy' ? 'success' : 'danger'}`}>
                                                            {transaction.side}
                                                        </span>
                                                    </td>
                                                    <td>{transaction.quantity}</td>
                                                    <td style={{ fontWeight: "bold", color: "#2c3e50", textAlign: "right" }}>
                                                        {transaction.price.toLocaleString("fr-FR")} Fcfa
                                                    </td>
                                                    <td style={{ color: "#2c3e50", fontWeight: "bold", textAlign: "right" }}>
                                                        {new Date(transaction.creationDate).toLocaleDateString("fr-FR", {
                                                            weekday: "short",
                                                            day: "2-digit",
                                                            month: "long",
                                                            year: "numeric",
                                                        })}
                                                    </td>
                                                    <td>
                                                        <span className={`badge rounded-pill ${getStatusClass(transaction.status)}`}>
                                                            {transaction.status}
                                                        </span>
                                                    </td>
                                                    <td className="pe-3">
                                                        <div className="d-flex align-items-center justify-content-end">
                                                            <Dropdown>
                                                                <Dropdown.Toggle variant="primary" className="tp-btn sharp">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 24 24" version="1.1">
                                                                        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                                            <rect x="0" y="0" width="24" height="24"></rect>
                                                                            <circle fill="#2A353A" cx="12" cy="5" r="2"></circle>
                                                                            <circle fill="#2A353A" cx="12" cy="12" r="2"></circle>
                                                                            <circle fill="#2A353A" cx="12" cy="19" r="2"></circle>
                                                                        </g>
                                                                    </svg>
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu className="dropdown-menu-end">
                                                                    <Dropdown.Item href="#" onClick={() => handleViewTransaction(transaction.id)}>Voir +</Dropdown.Item>
                                                                    <Dropdown.Item onClick={()=> handleDeleteClick(transaction)}>Supprimer</Dropdown.Item>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="text-center text-muted py-3 text-danger">Aucune transaction trouvée.</div>
                                )}
                            </div>

                            {noTransactionsMessage && (
                                <div className="text-danger text-center" key="no-transactions">
                                    {noTransactionsMessage}
                                </div>
                            )}
                        </div>

                        {!loading && !error && transactions.length > 0 && (
                            <nav>
                                <ul className="pagination justify-content-center">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <Button
                                            className="page-link"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="fa-solid fa-angle-left"></i>
                                        </Button>
                                    </li>

                                    {[...Array(totalPages)].map((_, index) => (
                                        <li
                                            key={index + 1}
                                            className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                        >
                                            <Button
                                                className="page-link"
                                                onClick={() => handlePageChange(index + 1)}
                                            >
                                                {index + 1}
                                            </Button>
                                        </li>
                                    ))}

                                    <li
                                        className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                                    >
                                        <Button
                                            className="page-link"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            <i className="fa-solid fa-angle-right"></i>
                                        </Button>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </div>
                </div>
            </div>
            {/* modal de suppression de la transaction  */}
            {showDeleteModal && (
                <ConfirmationModal
                    message={'Etez-vous sûr de vouloir vraiment supprimer cette transaction ? cette action est irreverssible.'}
                    onConfirm={handleConfirmationDelete}
                    onCancel={handleCancelDelete}
                />
            )}

            {/* Modal Bootstrap pour afficher les détails de la transaction */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Détails de la Transaction</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedTransaction && (
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-md-6">
                                    <p><strong>ID:</strong> {selectedTransaction.id}</p>
                                    <p><strong>Order ID:</strong> {selectedTransaction.orderId}</p>
                                    <p><strong>Side:</strong> {selectedTransaction.side}</p>
                                    <p><strong>Créateur:</strong> {selectedTransaction.creatorName}</p>
                                    <p><strong>De Compte:</strong> {selectedTransaction.fromAccountEmail}</p>
                                </div>
                                <div className="col-md-6">
                                    <p><strong>À Compte:</strong> {selectedTransaction.toAccountEmail}</p>
                                    <p><strong>Prix:</strong> {selectedTransaction.price}</p>
                                    <p><strong>Quantité:</strong> {selectedTransaction.quantity}</p>
                                    <p><strong>Statut:</strong> {selectedTransaction.status}</p>
                                    <p><strong>Date Création:</strong> {new Date(selectedTransaction.creationDate).toLocaleString()}</p>
                                </div>
                            </div>
                            {/* Ajoutez d'autres détails ici */}
                        </div>

                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}